/**
 * src/gm/settlement/settle.ts — 结算有序落盘 + 脏标记幂等恢复（剧本 §6.7–6.9 继承）。
 *
 * 落盘顺序（硬性）：
 *   ⓪ 写 pending-settlement 脏标记（含 saveBefore 快照，供崩溃恢复幂等重放）
 *   ① 合并评审 + 写 reviews wrapper + 回填 review_id
 *   ② 先把项目 status → completed（中断恢复时 completed 被跳过，防双倍结算）
 *   ③ 更新 save：funds += P、exp、total_token_cost、projects_completed；升级检查；低保检查
 *   ④ 项目 token_usage 填入 API 真实 usage；团队三员工归还 hired
 *   ⑤ 清除脏标记
 *
 * 与旧版差异：存档读写走 SaveStorePort 端口（设计稿 §8.1），审计走事件流。
 */

import { PENDING_SETTLEMENT_KEY } from '../platform/config'
import { GmError } from '../platform/errors'
import { gmLockName, settleLockName, withProjectLock } from '../platform/locks'
import { readJson, removeKey, writeJson } from '../platform/storage'
import { audit } from '../events/event-log'
import type { ReviewSubscore } from '../employees/parser'
import type {
  Employee,
  MarketData,
  Project,
  Review,
  SaveData,
  SaveStorePort,
  TokenUsage,
} from '../platform/save-types'
import { calcCost, calcExpGained, calcRevenue, calcWeightedTotal, levelFor } from './formulas'

// ── 脏标记 ──────────────────────────────────────────────────────────────────

export interface PendingSettlement {
  projectId: string
  /** 已合并的完整评审对象（五数值字段已算好） */
  review: Review
  tokenUsage: TokenUsage
  /** 结算前 save 快照：恢复时先回滚到此再幂等重放，杜绝双倍结算 */
  saveBefore: SaveData
  createdAt: string
}

export function readPendingSettlement(): PendingSettlement | null {
  return readJson<PendingSettlement>(PENDING_SETTLEMENT_KEY)
}

function writePendingSettlement(p: PendingSettlement): void {
  writeJson(PENDING_SETTLEMENT_KEY, p)
}

export function clearPendingSettlement(): void {
  removeKey(PENDING_SETTLEMENT_KEY)
}

// ── 评审合并 ────────────────────────────────────────────────────────────────

/** 把评审分项与结算数值合并为完整评审对象（数值是结算的活，评审只出分项） */
export function buildReview(
  project: Project,
  employees: Employee[],
  market: MarketData,
  sub: ReviewSubscore,
  actualTokens: number,
): Review {
  const weightedTotal = calcWeightedTotal(sub.scores, market.dimension_weights)
  const revenue = calcRevenue(weightedTotal, market)
  const cost = calcCost(project.team, employees, market)
  return {
    id: `rev_${project.id.replace(/^proj_/, '')}`,
    project_id: project.id,
    scores: sub.scores,
    weighted_total: weightedTotal,
    market_fit_notes: sub.market_fit_notes,
    revenue,
    cost,
    actual_tokens: actualTokens,
    profit: revenue - cost,
    exp_gained: calcExpGained(weightedTotal),
    overall_comment: sub.overall_comment,
  }
}

// ── settle()：有序落盘 ──────────────────────────────────────────────────────

export interface SettleResult {
  review: Review
  leveledTo: number | null // 有升级则为新等级
  bailedOut: boolean
  saveAfter: SaveData
}

/**
 * 执行结算。幂等：带 pending 标记重入时先把 save 回滚到 saveBefore 再重放。
 * 跨标签页互斥：settle 全程持 Web Locks 锁（锁名带项目 id 域），拿不到锁直接
 * 报错——双标签页并发结算 = 双倍盈亏，必须硬性挡住。
 * @param store   存档端口（外壳实现）
 * @param pending 恢复场景直接传入已读出的脏标记；正常场景传 null 由本函数构造
 */
export async function settle(
  store: SaveStorePort,
  pending: PendingSettlement | null,
  normal?: { projectId: string; review: Review; tokenUsage: TokenUsage },
): Promise<SettleResult> {
  const lockProjectId = pending?.projectId ?? normal?.projectId
  if (!lockProjectId) throw new GmError('BAD_STATUS', '结算引擎点火失败：缺结算参数。')
  return withProjectLock(settleLockName(lockProjectId), async () => {
    const bundle = await store.readAll()
    let marker = pending
    if (!marker) {
      if (!normal) throw new GmError('BAD_STATUS', '结算引擎点火失败：缺结算参数。')
      const project = bundle.projects.projects.find((p) => p.id === normal.projectId)
      if (!project) throw new GmError('PROJECT_NOT_FOUND', '项目档案不见了，结算无从下手。')
      marker = {
        projectId: normal.projectId,
        review: normal.review,
        tokenUsage: normal.tokenUsage,
        saveBefore: bundle.save,
        createdAt: new Date().toISOString(),
      }
      writePendingSettlement(marker)
    }

    const { review, tokenUsage } = marker
    const projects = [...bundle.projects.projects]
    const pIdx = projects.findIndex((p) => p.id === marker.projectId)
    if (pIdx < 0) {
      clearPendingSettlement()
      throw new GmError('PROJECT_NOT_FOUND', '项目档案不见了，结算无从下手。')
    }
    const project = projects[pIdx]

    // 恢复重放：先把 save 回滚到结算前快照（无论上次崩在哪一步，都从干净起点重放）
    if (pending) {
      await store.writeBatch({ save: pending.saveBefore })
      audit(marker.projectId, 'save', '整档回滚到结算前快照', '上次结算盖到一半的章，回滚后补完')
    }

    // ① 评审写入 reviews wrapper + 回填 review_id（已存在则跳过——幂等）
    const reviews = [...bundle.reviews.reviews]
    if (!reviews.some((r) => r.id === review.id)) {
      reviews.push(review)
    }
    projects[pIdx] = { ...projects[pIdx], review_id: review.id }
    await store.writeBatch({ reviews: { reviews }, projects: { projects } })
    audit(marker.projectId, 'reviews', `+1 条（${review.id}，总分 ${review.weighted_total}）`, '评审落盘并回填 review_id')

    // ② 先把项目 status → completed（硬性顺序：先于 save 更新，防双倍结算）
    projects[pIdx] = { ...projects[pIdx], status: 'completed' }
    await store.writeBatch({ projects: { projects } })
    audit(marker.projectId, 'projects', `${project.id}.status → completed`, '结算顺序硬性规定：先封盘再动账')

    // ③ save 更新：funds += P、exp、total_token_cost、projects_completed；升级；低保
    const before = pending ? pending.saveBefore : marker.saveBefore
    let funds = before.funds + review.profit
    const exp = before.exp + review.exp_gained
    const newLevel = levelFor(exp, before.level_unlocks)
    const leveledTo = newLevel > before.level ? newLevel : null
    let bailedOut = false
    const bailout = { ...before.bailout }
    if (funds < bailout.threshold) {
      funds = bailout.grant_to
      bailout.times_used += 1
      bailedOut = true
    }
    const saveAfter: SaveData = {
      ...before,
      funds,
      exp,
      level: leveledTo ?? before.level,
      total_token_cost: before.total_token_cost + tokenUsage.total,
      projects_completed: before.projects_completed + 1,
      bailout,
    }
    await store.writeBatch({ save: saveAfter })
    audit(
      marker.projectId,
      'save',
      `funds ${before.funds}→${funds}（P=${review.profit}），exp +${review.exp_gained}，token +${tokenUsage.total}` +
        (leveledTo ? `，升级到 Lv.${leveledTo}` : '') +
        (bailedOut ? `，低保第 ${bailout.times_used} 次触发` : ''),
      '盈亏 / 经验 / 真实 token 记账落盘',
    )

    // ④ 项目 token_usage 填入真实 usage；团队三员工归还 hired
    projects[pIdx] = { ...projects[pIdx], token_usage: tokenUsage }
    const teamIds = new Set([project.team.designer, project.team.programmer, project.team.artist])
    const employees = bundle.employees.employees.map((e) =>
      teamIds.has(e.id) ? { ...e, status: 'hired' as const } : e,
    )
    await store.writeBatch({ projects: { projects }, employees: { employees } })
    audit(marker.projectId, 'employees', '团队三员工 status → hired', '项目封盘，员工归队')
    audit(marker.projectId, 'projects', `${project.id}.token_usage 三岗 + total`, '真实 token 消耗回填')

    // ⑤ 清除脏标记
    clearPendingSettlement()

    return { review, leveledTo, bailedOut, saveAfter }
  })
}

// 再导出锁名供内核复用（避免内核直接依赖 locks 的命名细节）
export { gmLockName }
